import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Barcode,
  Bell,
  CheckCircle,
  Wrench,
  MapPin,
  Clock,
} from '@phosphor-icons/react';

export const RFIDTrackerPage = () => {
  const { api } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkFeatureStatus();
  }, []);

  const checkFeatureStatus = async () => {
    try {
      const response = await api.get('/rfid/status');
      setEnabled(response.data?.enabled || false);
    } catch (error) {
      console.error('Failed to check RFID status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-10 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="p-6 lg:p-10 animate-fade-in" data-testid="rfid-page">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-100 rounded-2xl mb-6">
            <Barcode size={40} className="text-slate-400" />
          </div>
          
          <Badge className="bg-amber-100 text-amber-800 mb-4">Coming Soon</Badge>
          
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            RFID Tool Tracker
          </h1>
          
          <p className="text-slate-600 text-lg mb-8">
            Track equipment and tool assignments with RFID technology.
            This feature is currently in development.
          </p>

          <Card className="border-slate-200 text-left mb-8">
            <CardHeader>
              <CardTitle className="font-display">Planned Features</CardTitle>
              <CardDescription>What to expect when RFID Tracker launches</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Wrench size={18} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Tool Inventory Management</p>
                    <p className="text-sm text-slate-500">Track all tools with RFID tags</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Real-time Location Tracking</p>
                    <p className="text-sm text-slate-500">Know where your equipment is at all times</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={18} className="text-violet-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Check-in/Check-out System</p>
                    <p className="text-sm text-slate-500">Automated tool assignment and returns</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock size={18} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Usage History & Reports</p>
                    <p className="text-sm text-slate-500">Detailed tracking of tool usage over time</p>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Button variant="outline" className="gap-2" data-testid="notify-btn">
            <Bell size={18} />
            Notify me when available
          </Button>
        </div>
      </div>
    );
  }

  // If enabled (future implementation)
  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="rfid-page-enabled">
      <div className="mb-8">
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
          RFID Tool Tracker
        </h1>
        <p className="text-slate-600">
          Track and manage equipment with RFID technology
        </p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-6 text-center">
          <Barcode size={48} className="text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Tool inventory will appear here</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default RFIDTrackerPage;
